/**
 * AdminTable — Admin CRUD 리스트 공통 테이블
 *
 * Table 컴포넌트를 감싸고, toggle(optimistic)/action/delete 기능을 내장한다.
 */

import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react'
import { Table, type TableColumn } from './Table'
import { Toggle } from './Toggle'
import { Button } from './Button'
import { ConfirmDialog } from './ConfirmDialog'
import { useToast } from '../components/ToastContainer'
import styles from './AdminTable.module.css'

// ── 인터페이스 ──

export interface AdminTableToggle<T> {
  label: string
  width?: string
  getValue: (row: T) => boolean
  onToggle: (row: T, newValue: boolean) => Promise<void>
  disabled?: (row: T) => boolean
}

export interface AdminTableAction<T> {
  label: string
  variant?: 'ghost' | 'secondary' | 'primary' | 'danger'
  onClick: (row: T) => void | Promise<void>
  hidden?: (row: T) => boolean
  loading?: (row: T) => boolean
}

export interface AdminTableDelete<T> {
  message: (row: T) => string
  onDelete: (row: T) => Promise<void>
  hidden?: (row: T) => boolean
  successMessage?: string
}

export interface AdminTableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  rowKey: (row: T) => string
  toggles?: AdminTableToggle<T>[]
  actions?: AdminTableAction<T>[]
  delete?: AdminTableDelete<T>
  isLoading?: boolean
  emptyMessage?: string
  size?: 'sm' | 'md'
  hoverable?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (key: string, order: 'asc' | 'desc') => void
  onRowClick?: (row: T) => void
}

// ── 컴포넌트 ──

export function AdminTable<T>({
  data,
  columns,
  rowKey,
  toggles,
  actions,
  delete: deleteDef,
  isLoading,
  emptyMessage,
  size = 'sm',
  hoverable = true,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
}: AdminTableProps<T>) {
  const { addToast } = useToast()

  // Optimistic toggle overrides: Map<`${rowId}:${toggleIdx}`, boolean>
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map())
  // Loading keys: Set<`${rowId}:toggle:${idx}` | `${rowId}:action:${idx}` | `${rowId}:delete`>
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set())
  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState<T | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // data가 변경되면 overrides 초기화 (부모 refetch 후)
  const prevDataRef = useRef(data)
  useEffect(() => {
    if (prevDataRef.current !== data) {
      prevDataRef.current = data
      setOverrides(new Map())
    }
  }, [data])

  // ── Toggle 핸들러 ──

  const handleToggle = useCallback(
    async (row: T, toggleIdx: number, toggle: AdminTableToggle<T>) => {
      const id = rowKey(row)
      const overrideKey = `${id}:${toggleIdx}`
      const loadingKey = `${id}:toggle:${toggleIdx}`
      const currentValue = toggle.getValue(row)
      const newValue = !currentValue

      // 1. Optimistic update
      setOverrides((prev) => {
        const next = new Map(prev)
        next.set(overrideKey, newValue)
        return next
      })
      // 2. Disable toggle (loading)
      setLoadingKeys((prev) => {
        const next = new Set(prev)
        next.add(loadingKey)
        return next
      })

      try {
        await toggle.onToggle(row, newValue)
        // 성공 — override 유지 (부모 refetch로 data 갱신 시 자동 해제)
      } catch (err: any) {
        // 실패 — rollback
        setOverrides((prev) => {
          const next = new Map(prev)
          next.delete(overrideKey)
          return next
        })
        addToast({ type: 'error', message: err.message || '변경에 실패했습니다' })
      } finally {
        setLoadingKeys((prev) => {
          const next = new Set(prev)
          next.delete(loadingKey)
          return next
        })
      }
    },
    [rowKey, addToast],
  )

  // ── Delete 핸들러 ──

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget || !deleteDef) return
    setDeleteLoading(true)
    try {
      await deleteDef.onDelete(deleteTarget)
      addToast({ type: 'success', message: deleteDef.successMessage ?? '삭제되었습니다' })
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '삭제에 실패했습니다' })
    } finally {
      setDeleteLoading(false)
      setDeleteTarget(null)
    }
  }, [deleteTarget, deleteDef, addToast])

  // ── 컬럼 조립 ──

  const allColumns: TableColumn<T>[] = [...columns]

  // Toggle 컬럼
  if (toggles) {
    toggles.forEach((toggle, idx) => {
      allColumns.push({
        key: `__toggle_${idx}`,
        label: toggle.label,
        width: toggle.width ?? '70px',
        align: 'center',
        render: (row: T) => {
          const id = rowKey(row)
          const overrideKey = `${id}:${idx}`
          const loadingKey = `${id}:toggle:${idx}`
          const currentValue = overrides.has(overrideKey)
            ? overrides.get(overrideKey)!
            : toggle.getValue(row)
          const isDisabled = loadingKeys.has(loadingKey) || toggle.disabled?.(row) === true

          return (
            <Toggle
              checked={currentValue}
              onChange={() => handleToggle(row, idx, toggle)}
              size="sm"
              disabled={isDisabled}
            />
          )
        },
      })
    })
  }

  // Actions + Delete 컬럼
  const hasActions = (actions && actions.length > 0) || deleteDef
  if (hasActions) {
    allColumns.push({
      key: '__actions',
      label: '',
      width: '140px',
      align: 'right',
      render: (row: T) => {
        const actionButtons: ReactNode[] = []

        if (actions) {
          actions.forEach((action, idx) => {
            if (action.hidden?.(row)) return
            const id = rowKey(row)
            const loadingKey = `${id}:action:${idx}`
            const isActionLoading = loadingKeys.has(loadingKey) || action.loading?.(row) === true
            actionButtons.push(
              <Button
                key={`action-${idx}`}
                size="sm"
                variant={action.variant ?? 'ghost'}
                onClick={async () => {
                  setLoadingKeys((prev) => {
                    const next = new Set(prev)
                    next.add(loadingKey)
                    return next
                  })
                  try {
                    await action.onClick(row)
                  } finally {
                    setLoadingKeys((prev) => {
                      const next = new Set(prev)
                      next.delete(loadingKey)
                      return next
                    })
                  }
                }}
                loading={isActionLoading}
              >
                {action.label}
              </Button>,
            )
          })
        }

        if (deleteDef && !deleteDef.hidden?.(row)) {
          actionButtons.push(
            <Button
              key="delete"
              size="sm"
              variant="ghost"
              onClick={() => setDeleteTarget(row)}
            >
              삭제
            </Button>,
          )
        }

        if (actionButtons.length === 0) return null

        return <div className={styles.actionCell}>{actionButtons}</div>
      },
    })
  }

  return (
    <>
      <Table<T>
        columns={allColumns}
        data={data}
        rowKey={rowKey}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        size={size}
        hoverable={hoverable}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={onSort}
        onRowClick={onRowClick}
      />
      {deleteDef && (
        <ConfirmDialog
          open={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="삭제 확인"
          message={deleteTarget ? deleteDef.message(deleteTarget) : ''}
          confirmLabel="삭제"
          variant="danger"
          loading={deleteLoading}
        />
      )}
    </>
  )
}
